import { Button } from './ui/button';
import { Card } from './ui/card';
import { 
  Activity, 
  Calendar, 
  Heart, 
  FileText, 
  Brain, 
  TrendingUp,
  Package,
  BarChart3
} from 'lucide-react';

interface EmptyStateProps {
  onAction?: () => void;
  actionLabel?: string;
}

export function EmptyCheckIns({ onAction, actionLabel = 'Start First Check-In' }: EmptyStateProps) {
  return (
    <Card className="p-12 text-center space-y-6" role="region" aria-label="No check-ins available">
      <div className="w-16 h-16 mx-auto bg-neutral-100 rounded-full flex items-center justify-center">
        <Activity className="w-8 h-8 text-neutral-400" aria-hidden="true" />
      </div>
      <div className="space-y-2">
        <h2 className="text-xl font-light text-neutral-900">No Check-Ins Yet</h2>
        <p className="text-neutral-600 max-w-md mx-auto">
          Complete your first daily check-in to start tracking your Relational Tension Index.
          It takes about 60 seconds.
        </p>
      </div>
      {onAction && (
        <Button onClick={onAction} size="lg">
          {actionLabel}
        </Button>
      )}
    </Card>
  );
}

export function EmptyTimeline({ onAction, actionLabel = 'Complete First Check-In' }: EmptyStateProps) {
  return (
    <Card className="p-12 text-center space-y-6" role="region" aria-label="No timeline data available">
      <div className="w-16 h-16 mx-auto bg-neutral-100 rounded-full flex items-center justify-center">
        <Calendar className="w-8 h-8 text-neutral-400" aria-hidden="true" />
      </div>
      <div className="space-y-2">
        <h2 className="text-xl font-light text-neutral-900">No Timeline Data</h2>
        <p className="text-neutral-600 max-w-md mx-auto">
          Your RTI timeline will appear here after you complete check-ins.
          Track patterns and trends over days, weeks, and months.
        </p>
      </div>
      {onAction && (
        <Button onClick={onAction} size="lg">
          {actionLabel}
        </Button>
      )}
    </Card>
  );
}

export function EmptyRelationships({ onAction, actionLabel = 'Add Relationship' }: EmptyStateProps) {
  return (
    <Card className="p-12 text-center space-y-6" role="region" aria-label="No relationships available">
      <div className="w-16 h-16 mx-auto bg-pink-100 rounded-full flex items-center justify-center">
        <Heart className="w-8 h-8 text-pink-400" aria-hidden="true" />
      </div>
      <div className="space-y-2">
        <h2 className="text-xl font-light text-neutral-900">No Relationships Yet</h2>
        <p className="text-neutral-600 max-w-md mx-auto">
          Add your first relationship to start tracking tension dynamics.
          You can track multiple relationships separately.
        </p>
      </div>
      {onAction && (
        <Button onClick={onAction} size="lg">
          {actionLabel}
        </Button>
      )}
    </Card>
  );
}

export function EmptyJournal({ onAction, actionLabel = 'Write First Entry' }: EmptyStateProps) {
  return (
    <Card className="p-12 text-center space-y-6" role="region" aria-label="No journal entries available">
      <div className="w-16 h-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center">
        <FileText className="w-8 h-8 text-blue-400" aria-hidden="true" />
      </div>
      <div className="space-y-2">
        <h2 className="text-xl font-light text-neutral-900">No Journal Entries</h2>
        <p className="text-neutral-600 max-w-md mx-auto">
          Add context to your check-ins with journal entries.
          Capture what happened, how you felt, and track patterns over time.
        </p>
      </div>
      {onAction && (
        <Button onClick={onAction} size="lg">
          {actionLabel}
        </Button>
      )}
    </Card>
  );
}

export function EmptyAnxietyTracker() {
  return (
    <div className="bg-purple-50 border border-purple-200 rounded-lg p-8 text-center space-y-4">
      <div className="w-12 h-12 mx-auto bg-purple-100 rounded-full flex items-center justify-center">
        <Brain className="w-6 h-6 text-purple-400" />
      </div>
      <div className="space-y-2">
        <h4 className="text-lg font-light text-neutral-900">No Anxiety Logs Yet</h4>
        <p className="text-sm text-neutral-600 max-w-sm mx-auto">
          Start logging anxious thoughts and predictions.
          You'll be able to track whether they come true.
        </p>
      </div>
    </div>
  );
}

export function EmptyAttachmentJourney() {
  return (
    <div className="bg-pink-50 border border-pink-200 rounded-lg p-8 text-center space-y-4">
      <div className="w-12 h-12 mx-auto bg-pink-100 rounded-full flex items-center justify-center">
        <Heart className="w-6 h-6 text-pink-400" />
      </div>
      <div className="space-y-2">
        <h4 className="text-lg font-light text-neutral-900">Begin Your Attachment Journey</h4>
        <p className="text-sm text-neutral-600 max-w-sm mx-auto">
          Take your first attachment style assessment to start tracking your evolution
          toward secure attachment.
        </p>
      </div>
    </div>
  );
}

export function EmptyInsights() {
  return (
    <Card className="p-12 text-center space-y-6">
      <div className="w-16 h-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center">
        <TrendingUp className="w-8 h-8 text-blue-400" />
      </div>
      <div className="space-y-2">
        <h3 className="text-xl font-light text-neutral-900">Not Enough Data Yet</h3>
        <p className="text-neutral-600 max-w-md mx-auto">
          Complete at least 7 check-ins to unlock pattern insights and behavioral analysis.
        </p>
      </div>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto">
        <p className="text-sm text-blue-900">
          <strong>Coming soon:</strong> Initiation patterns, peak tension times, withdrawal trends, and personalized recommendations.
        </p>
      </div>
    </Card>
  );
}

export function EmptySituationalPacks({ onAction, actionLabel = 'Browse Packs' }: EmptyStateProps) {
  return (
    <Card className="p-12 text-center space-y-6">
      <div className="w-16 h-16 mx-auto bg-purple-100 rounded-full flex items-center justify-center">
        <Package className="w-8 h-8 text-purple-400" />
      </div>
      <div className="space-y-2">
        <h3 className="text-xl font-light text-neutral-900">No Active Packs</h3>
        <p className="text-neutral-600 max-w-md mx-auto">
          Situational Packs provide specialized daily action plans for specific challenges
          like breakup recovery, reconnection, or conflict repair.
        </p>
      </div>
      {onAction && (
        <Button onClick={onAction} size="lg">
          {actionLabel}
        </Button>
      )}
    </Card>
  );
}

export function EmptyWeeklyReport() {
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-8 text-center space-y-4">
      <div className="w-12 h-12 mx-auto bg-blue-100 rounded-full flex items-center justify-center">
        <BarChart3 className="w-6 h-6 text-blue-400" />
      </div>
      <div className="space-y-2">
        <h4 className="text-lg font-light text-neutral-900">Complete 7 Days of Check-Ins</h4>
        <p className="text-sm text-neutral-600 max-w-sm mx-auto">
          Your weekly report card will appear after you have at least 7 days of check-in data.
        </p>
      </div>
    </div>
  );
}

interface LoadingStateProps {
  message?: string;
}

export function LoadingState({ message = 'Loading...' }: LoadingStateProps) {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center space-y-4">
        <div className="w-12 h-12 mx-auto border-4 border-neutral-200 border-t-violet-600 rounded-full animate-spin" />
        <p className="text-neutral-600">{message}</p>
      </div>
    </div>
  );
}

interface ErrorStateProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
}

export function ErrorState({ 
  title = 'Something went wrong', 
  message = 'Please try again later.',
  onRetry 
}: ErrorStateProps) {
  return (
    <Card className="p-12 text-center space-y-6">
      <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center">
        <Activity className="w-8 h-8 text-red-400" />
      </div>
      <div className="space-y-2">
        <h3 className="text-xl font-light text-neutral-900">{title}</h3>
        <p className="text-neutral-600 max-w-md mx-auto">{message}</p>
      </div>
      {onRetry && (
        <Button onClick={onRetry} variant="outline">
          Try Again
        </Button>
      )}
    </Card>
  );
}